// Project case studies. Images live in src/images/projects/<slug>/ — `card.jpg` is the
// 800x500 grid thumbnail, the numbered files are the full-size gallery shots.

import hockleyCard from "../../images/projects/hockley-mills/card.jpg";
import hockley01 from "../../images/projects/hockley-mills/01-elevation-railway.jpg";
import hockley02 from "../../images/projects/hockley-mills/02-courtyard-elevation.jpg";
import hockley03 from "../../images/projects/hockley-mills/03-brick-facade-windows.jpg";
import hockley04 from "../../images/projects/hockley-mills/04-goods-yard-gable.jpg";
import hockley05 from "../../images/projects/hockley-mills/05-jewellery-quarter-station.jpg";

import masonCard from "../../images/projects/mason-halls/card.jpg";
import mason01 from "../../images/projects/mason-halls/01-cladding-install-progress.jpg";
import mason02 from "../../images/projects/mason-halls/02-insulation-scissor-lift.jpg";
import mason03 from "../../images/projects/mason-halls/03-insulation-cladding-corner.jpg";
import mason04 from "../../images/projects/mason-halls/04-site-overview.jpg";
import mason05 from "../../images/projects/mason-halls/05-curved-facade-detail.jpg";
import mason06 from "../../images/projects/mason-halls/06-completed-elevation.jpg";

import redcliffCard from "../../images/projects/redcliff-quarter/card.jpg";
import redcliff01 from "../../images/projects/redcliff-quarter/01-completed-elevation.jpg";
import redcliff02 from "../../images/projects/redcliff-quarter/02-curtain-walling-install.jpg";
import redcliff03 from "../../images/projects/redcliff-quarter/03-glazed-bays-detail.jpg";
import redcliff04 from "../../images/projects/redcliff-quarter/04-street-view.jpg";

import nqccCard from "../../images/projects/nqcc/card.jpg";
import nqcc01 from "../../images/projects/nqcc/01-curtain-walling-elevation.jpg";
import nqcc02 from "../../images/projects/nqcc/02-glazing-install-progress.jpg";
import nqcc03 from "../../images/projects/nqcc/03-structural-frame-lift.jpg";
import nqcc04 from "../../images/projects/nqcc/04-solar-fin-screen.jpg";

import merchantsCard from "../../images/projects/merchants-venturers/card.jpg";
import merchants01 from "../../images/projects/merchants-venturers/01-courtyard.jpg";
import merchants02 from "../../images/projects/merchants-venturers/02-windows-doors-elevation.jpg";
import merchants03 from "../../images/projects/merchants-venturers/03-walkway-elevation.jpg";

import wargraveCard from "../../images/projects/wargrave-manor/card.jpg";
import wargrave01 from "../../images/projects/wargrave-manor/01-entrance-elevation.jpg";
import wargrave02 from "../../images/projects/wargrave-manor/02-gable-elevation.jpg";
import wargrave03 from "../../images/projects/wargrave-manor/03-single-storey-wing.jpg";
import wargrave04 from "../../images/projects/wargrave-manor/04-curtain-walling-install.jpg";

import confidentialCard from "../../images/projects/confidential/card.jpg";
import confidential01 from "../../images/projects/confidential/01-secure-facade.jpg";

const projects = [
  {
    title: "Hockley Mills",
    subtitle: "Jewellery Quarter, Birmingham",
    location: "Birmingham",
    sector: "Residential / Mixed Use",
    imgSrc: hockleyCard,
    imgAlt: "Hockley Mills — completed brick and aluminium facade alongside the railway",
    images: [
      { url: hockley01, alt: "The completed Hockley Mills elevation running alongside the railway line" },
      { url: hockley02, alt: "Courtyard elevation with Kawneer AA720 windows installed" },
      { url: hockley03, alt: "Brick facade with arched and rectangular window openings glazed" },
      { url: hockley04, alt: "The Goods Yard gable, looking up at the installed facade" },
      { url: hockley05, alt: "Hockley Mills above the Jewellery Quarter station entrance" },
    ],
    description:
      "Around 7,000 m² of Kawneer aluminium windows, doors and curtain walling on a 395-apartment development in Birmingham's Jewellery Quarter.",
    fullDescription: [
      "AV Facade Services Ltd is proud to have been involved in the delivery of the facade package at Hockley Mills, a major development located in Birmingham's historic Jewellery Quarter.",
      "We originally commenced works on the project as a specialist facade installation subcontractor for Norman & Underwood Ltd, installing approximately 7,000 m² of Kawneer aluminium facade systems, including AA720 windows and AA100 curtain walling, together with doors and associated facade works.",
      "Unfortunately, during the course of the project, Norman & Underwood Ltd entered administration. Following this, we were approached by the main contractor, Sir Robert McAlpine, and asked to continue our works and help bring the facade installation package through to completion.",
      "We are extremely grateful to Sir Robert McAlpine for placing their trust in AV Facade Services Ltd and giving us the opportunity to continue working on such a significant project. Being asked to remain on the project following a major change in the supply chain is something we are particularly proud of and reflects the confidence placed in our team, workmanship and ability to deliver.",
      "Once complete, Hockley Mills will provide 395 new apartments alongside a new entrance to the railway station, making a significant contribution to the continued regeneration of Birmingham's Jewellery Quarter.",
    ],
    highlights: [
      "Kawneer AA720 aluminium windows",
      "Kawneer AA100 curtain walling",
      "Aluminium door sets",
      "Associated facade works",
    ],
    details: [
      { label: "Main Contractor", value: "Sir Robert McAlpine" },
      { label: "Original Client", value: "Norman & Underwood Ltd" },
      { label: "Location", value: "Jewellery Quarter, Birmingham" },
      { label: "Sector", value: "Residential / Mixed Use" },
      { label: "Development", value: "395 apartments & new railway station entrance" },
      { label: "Facade Area", value: "Approx. 7,000 m²" },
    ],
  },
  {
    title: "Mason Halls",
    subtitle: "University of Birmingham",
    location: "Birmingham",
    sector: "Education / Student Accommodation",
    imgSrc: masonCard,
    imgAlt: "Mason Halls — facade remediation works in progress on a student accommodation block",
    images: [
      { url: mason01, alt: "Cladding installation in progress, panels still in protective film" },
      { url: mason02, alt: "Insulation and cladding works from a scissor lift" },
      { url: mason03, alt: "Corner elevation part-way through insulation and cladding installation" },
      { url: mason04, alt: "Overview across the Mason Halls student accommodation buildings" },
      { url: mason05, alt: "Detail of the curved facade and window arrangement" },
      { url: mason06, alt: "Completed elevation following facade remediation works" },
    ],
    description:
      "Approximately 7,000 m² of facade remediation across six student accommodation buildings — cladding, insulation, copings and flashings.",
    fullDescription: [
      "AV Facade Services Ltd is proud to have been involved in the Mason Halls facade remediation project at the University of Birmingham.",
      "Working as a specialist facade installation subcontractor for Oak Specialist Services Ltd, with Galliford Try acting as the main contractor, our team has been involved in the extensive refurbishment and remediation works across six student accommodation buildings.",
      "The project comprises approximately 7,000 m² of facade remediation works, involving the removal and installation of multiple elements of the external building envelope.",
      "Delivering a project of this scale across six student accommodation buildings requires careful planning, coordination and consistent quality throughout every stage of the installation.",
      "We are proud to support Oak Specialist Services Ltd and Galliford Try in delivering this significant facade remediation project and appreciate the trust placed in AV Facade Services Ltd and our installation teams.",
    ],
    highlights: [
      "Removal and installation of cladding systems",
      "Installation of insulation",
      "Installation of copings",
      "Installation of flashings",
      "Associated facade remediation works",
    ],
    details: [
      { label: "Main Contractor", value: "Galliford Try" },
      { label: "Client", value: "Oak Specialist Services Ltd" },
      { label: "Location", value: "University of Birmingham" },
      { label: "Sector", value: "Education / Student Accommodation" },
      { label: "Project", value: "6 student accommodation buildings" },
      { label: "Facade Area", value: "Approx. 7,000 m²" },
    ],
  },
  {
    title: "Redcliff Quarter Phase 2",
    subtitle: "Bristol",
    location: "Bristol",
    sector: "Residential / Mixed Use",
    imgSrc: redcliffCard,
    imgAlt: "Redcliff Quarter Phase 2 — completed glazed elevation",
    images: [
      { url: redcliff01, alt: "Completed elevation with the glazed facade package installed" },
      { url: redcliff02, alt: "Curtain walling being installed into the concrete frame" },
      { url: redcliff03, alt: "Detail of the full-height glazed bays" },
      { url: redcliff04, alt: "Street view of the development during facade installation" },
    ],
    description:
      "Curtain walling and the aluminium door package on a 232-apartment city-centre regeneration scheme in the Redcliffe conservation area.",
    fullDescription: [
      "AV Facade Services Ltd is proud to have been involved in the delivery of facade works at Redcliff Quarter Phase 2 in Bristol.",
      "Working as a specialist facade installation subcontractor for Duplus Architectural Systems Ltd, with Galliford Try acting as the main contractor, our team was responsible for the installation of key elements of the glazed facade package.",
      "The development forms part of one of Bristol's major city-centre regeneration schemes and comprises 232 apartments, seven commercial units, car parking and cycle storage. The project combines new-build construction with the retention of an existing Victorian facade, creating a significant mixed-use development within the Redcliffe conservation area.",
      "Delivering facade works on a development of this scale required careful coordination, consistent workmanship and close cooperation with the wider project team.",
      "We are proud to have supported Duplus Architectural Systems Ltd and Galliford Try in the delivery of this significant Bristol project and appreciate the trust placed in AV Facade Services Ltd and our installation teams.",
    ],
    highlights: [
      "Curtain walling installation",
      "Aluminium door package",
      "Associated facade installation works",
    ],
    details: [
      { label: "Main Contractor", value: "Galliford Try" },
      { label: "Client", value: "Duplus Architectural Systems Ltd" },
      { label: "Location", value: "Bristol" },
      { label: "Sector", value: "Residential / Mixed Use" },
      { label: "Development", value: "232 apartments & 7 commercial units" },
    ],
  },
  {
    title: "National Quantum Computing Centre",
    subtitle: "Harwell Campus, Oxfordshire",
    location: "Harwell Campus, Oxfordshire",
    sector: "Science / Research",
    imgSrc: nqccCard,
    imgAlt: "National Quantum Computing Centre — Schüco curtain walling elevation",
    images: [
      { url: nqcc01, alt: "Schüco curtain walling elevation with glazing installed" },
      { url: nqcc02, alt: "Glazing installation in progress along the building elevation" },
      { url: nqcc03, alt: "Crane lift to the structural frame during facade works" },
      { url: nqcc04, alt: "Vertical fin screen to the completed external envelope" },
    ],
    description:
      "Schüco curtain walling, aluminium doors and GRC panels on a technically advanced research facility at Harwell Campus.",
    fullDescription: [
      "AV Facade Services Ltd was proud to be involved in the delivery of facade works at the National Quantum Computing Centre (NQCC) at Harwell Campus, near Didcot, Oxfordshire.",
      "We commenced works on the project as a specialist facade installation subcontractor for Norman & Underwood Ltd, with Wates Construction acting as the main contractor.",
      "Unfortunately, Norman & Underwood Ltd entered administration during the course of the project, which brought our involvement in the works to an end. We remain proud of the facade installation works completed by our team and of our contribution to such a significant and technically advanced development.",
      "The project demonstrates the experience of AV Facade Services Ltd in delivering complex facade packages involving high-performance curtain walling, aluminium doors and specialist external cladding systems.",
    ],
    highlights: [
      "Schüco curtain walling systems",
      "Aluminium doors",
      "GRC panels",
      "Associated facade installation works",
    ],
    details: [
      { label: "Main Contractor", value: "Wates Construction" },
      { label: "Client", value: "Norman & Underwood Ltd" },
      { label: "Location", value: "Harwell Campus, near Didcot, Oxfordshire" },
      { label: "Sector", value: "Science / Research" },
      { label: "Systems", value: "Schüco curtain walling" },
    ],
  },
  {
    title: "Merchants' & Venturers' Academy",
    subtitle: "Bristol",
    location: "Bristol",
    sector: "Education",
    imgSrc: merchantsCard,
    imgAlt: "Merchants' & Venturers' Academy — courtyard elevation with aluminium glazing",
    images: [
      { url: merchants01, alt: "Courtyard elevation with Metal Technology windows and doors installed" },
      { url: merchants02, alt: "Aluminium window and door elevation to the completed building" },
      { url: merchants03, alt: "Walkway elevation with the glazing package complete" },
    ],
    description:
      "Metal Technology aluminium windows, doors and curtain walling installed for Duplus Architectural Ltd at a Bristol academy.",
    fullDescription: [
      "AV Facade Services Ltd was appointed as a specialist facade installation subcontractor to Duplus Architectural Ltd for works at Merchants' & Venturers' Academy in Bristol.",
      "Our experienced installation team worked closely with Duplus Architectural Ltd to deliver the package safely, efficiently and to the required quality standards.",
      "This project demonstrates our experience in the installation of aluminium window, door and curtain wall systems across education and commercial developments.",
    ],
    highlights: [
      "Metal Technology aluminium windows",
      "Aluminium doors",
      "Curtain walling systems",
      "Associated facade installation works",
    ],
    details: [
      { label: "Client", value: "Duplus Architectural Ltd" },
      { label: "Location", value: "Bristol" },
      { label: "Sector", value: "Education" },
      { label: "Systems", value: "Metal Technology" },
    ],
  },
  {
    title: "Wargrave Manor",
    subtitle: "Stables & Guest House, Berkshire",
    location: "Wargrave, Berkshire",
    sector: "Private Residential",
    imgSrc: wargraveCard,
    imgAlt: "Wargrave Manor — completed entrance elevation with aluminium glazing",
    images: [
      { url: wargrave01, alt: "Completed entrance elevation with aluminium doors and glazing installed" },
      { url: wargrave02, alt: "Gable elevation of the Stables & Guest House following completion" },
      { url: wargrave03, alt: "Single-storey wing with the aluminium window and door package installed" },
      { url: wargrave04, alt: "Curtain walling installed to the full-height glazed elevation" },
    ],
    description:
      "The full aluminium glazing package — curtain walling, windows and doors — for the Stables & Guest House at a private Berkshire estate.",
    fullDescription: [
      "AV Facade Services Ltd is proud to have been involved in the Stables & Guest House development at Wargrave Manor, a prestigious private estate in Berkshire.",
      "Working as a specialist facade installation subcontractor for Duplus Architectural Systems Ltd, our team was responsible for the installation of the aluminium glazing package across the development.",
      "Working within the grounds of a historic estate required a high standard of workmanship and attention to detail, ensuring the modern glazing systems were installed in keeping with the quality and architectural character of the development.",
      "We are proud to have supported Duplus Architectural Systems Ltd in the delivery of this distinctive project and appreciate the continued trust placed in AV Facade Services Ltd and our installation teams.",
    ],
    highlights: [
      "Curtain walling systems",
      "Aluminium windows",
      "Aluminium doors",
      "Associated facade installation works",
    ],
    details: [
      { label: "Client", value: "Duplus Architectural Systems Ltd" },
      { label: "Location", value: "Wargrave, Berkshire" },
      { label: "Sector", value: "Private Residential" },
      { label: "Project", value: "Stables & Guest House" },
    ],
  },
  {
    title: "Confidential Project",
    subtitle: "Secure development",
    location: "Confidential",
    sector: "Secure / Confidential",
    imgSrc: confidentialCard,
    imgAlt: "A secure facade — project details withheld under confidentiality",
    images: [
      { url: confidential01, alt: "Illustrative image for the project due to confidentiality" },
    ],
    description:
      "A high-security development delivered with BPSS-checked operatives. Project details are withheld under our confidentiality obligations.",
    fullDescription: [
      "AV Facade Services Ltd is currently proud to be involved in the delivery of a high-security confidential project.",
      "Due to the sensitive nature of the development and our confidentiality obligations, we are unable to disclose the project name, location, client, scope or any further project-specific information.",
      "What we can share is that the project requires appropriately vetted personnel, and we have BPSS-checked operatives working on site to meet the project's security requirements.",
      "Our involvement demonstrates our ability to provide experienced facade installation teams for projects where professionalism, discretion, security compliance and confidentiality are essential.",
      "We are proud to have been trusted with this work and look forward to continuing to support the successful delivery of the project while maintaining the highest standards of confidentiality.",
    ],
    highlights: [
      "BPSS-checked operatives on site",
      "Security-compliant working practices",
      "Full confidentiality maintained throughout",
    ],
    details: [
      { label: "Project", value: "Confidential" },
      { label: "Client", value: "Confidential" },
      { label: "Location", value: "Confidential" },
      { label: "Sector", value: "Secure / Confidential" },
      { label: "Personnel", value: "BPSS-checked operatives" },
    ],
  },
];

export default projects;
